import type {
  NewsCategory,
  NewsGuarantorFilter,
  NewsPlatform,
  NewsStatus,
  NewsStripeFilter,
} from '../types/news.types';

/**
 * Vocabulaire et bornes du module « Nouveautés ». Les valeurs (plateformes,
 * rôles, longueurs) reprennent `api/src/news/news.constants.ts` : le serveur
 * refuse tout ce qui en sort, les changer ici seul ne sert à rien.
 */

export const NEWS_MANAGE_PERMISSION = 'news.manage';

/** Ordre d'affichage des plateformes dans le formulaire et la liste. */
export const NEWS_PLATFORMS: NewsPlatform[] = ['PRO', 'UPSELL', 'ADMIN', 'SAV'];

export const NEWS_PLATFORM_LABELS: Record<NewsPlatform, string> = {
  PRO: 'Outil pro',
  UPSELL: 'Plateforme upsell',
  ADMIN: 'Admin',
  SAV: 'Gestion SAV',
};

/** Qui voit la nouveauté sur chaque plateforme — affiché sous la case à cocher. */
export const NEWS_PLATFORM_AUDIENCES: Record<NewsPlatform, string> = {
  PRO: 'Les vendeurs et leurs équipes (utilisateurs externes)',
  UPSELL: 'Les équipes Affinicar qui traitent les leads upsell',
  ADMIN: "Les équipes Affinicar qui utilisent l'administration",
  SAV: 'Les équipes Affinicar qui gèrent les SAV',
};

/** Rôles ciblables sur l'outil pro (rôle externe normalisé par l'API en ADMIN / USER). */
const EXTERNAL_ROLE_TYPES = ['ADMIN', 'USER'] as const;

/** Rôles ciblables sur les plateformes internes. */
const INTERNAL_ROLE_TYPES = ['SUPER_ADMIN', 'SUPERVISOR', 'COMMERCIAL', 'UPSELL'] as const;

/** Rôles qu'une cible peut viser, selon sa plateforme (cf. `allowedRoleTypesFor` côté API). */
export function allowedRoleTypesFor(platform: NewsPlatform): readonly string[] {
  return platform === 'PRO' ? EXTERNAL_ROLE_TYPES : INTERNAL_ROLE_TYPES;
}

/**
 * Libellés des rôles. `ADMIN` et `USER` n'existent que sur l'outil pro, les
 * quatre autres que sur les plateformes internes : aucune collision de clé.
 */
export const NEWS_ROLE_LABELS: Record<string, string> = {
  ADMIN: 'Administrateurs de la société',
  USER: 'Autres utilisateurs de la société',
  SUPER_ADMIN: 'Super admins',
  SUPERVISOR: 'Superviseurs',
  COMMERCIAL: 'Commerciaux',
  UPSELL: 'Profils upsell',
};

export const NEWS_GUARANTOR_FILTER_OPTIONS: { value: NewsGuarantorFilter; label: string }[] = [
  { value: 'ANY', label: 'Indifférent' },
  { value: 'OWN', label: 'Propre garant (le vendeur porte lui-même la garantie)' },
  { value: 'EXTERNAL', label: 'Garant externe (la garantie est portée par un tiers)' },
];

export const NEWS_STRIPE_FILTER_OPTIONS: { value: NewsStripeFilter; label: string }[] = [
  { value: 'ANY', label: 'Indifférent' },
  { value: 'WITH', label: 'Sociétés avec un compte Stripe' },
  { value: 'WITHOUT', label: 'Sociétés sans compte Stripe' },
];

/** Sens exact de « a un compte Stripe », affiché sous le filtre. */
export const NEWS_STRIPE_FILTER_HINT =
  "La société a un compte Stripe connecté — le sien ou celui dont elle dépend (groupe, société qui la facture) — quel que soit l'état de ce compte.";

export const NEWS_STATUS_LABELS: Record<NewsStatus, string> = {
  DRAFT: 'Brouillon',
  PUBLISHED: 'Publiée',
};

// Bornes de saisie (colonnes et DTO côté API).
export const NEWS_TITLE_MAX_LENGTH = 200;
export const NEWS_SHORT_DESCRIPTION_MAX_LENGTH = 500;
export const NEWS_CTA_LABEL_MAX_LENGTH = 80;
export const NEWS_URL_MAX_LENGTH = 1024;
export const NEWS_SLUG_MAX_LENGTH = 80;
export const NEWS_PRIORITY_MIN = -1000;
export const NEWS_PRIORITY_MAX = 1000;
/** Longueur maximale d'une recherche (liste, suivi par utilisateur et par société) : au-delà, l'API répond 400. */
export const NEWS_SEARCH_MAX_LENGTH = 100;
/** Longueur maximale du brief confié à l'assistant de rédaction (`NEWS_AI_BRIEF_MAX_LENGTH` côté API). */
export const NEWS_AI_BRIEF_MAX_LENGTH = 4000;

/** Minuscules et chiffres séparés par des tirets simples, sans tiret aux bords. */
export const NEWS_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Lien du bouton d'action : adresse http(s), ou chemin interne commençant par UN
 * seul « / » suivi d'autre chose qu'un « / » ou un « \ ». Les navigateurs
 * traitent « \ » comme « / » : « /\evil.com » se résout en « //evil.com », donc
 * sur un autre domaine — aucune barre oblique inversée n'est admise.
 *
 * COPIE EXACTE de `NEWS_CTA_URL_PATTERN` côté API (`api/src/news/dto/news.dto.ts`),
 * à maintenir identique. Sert aussi à la lecture (`getNewsCta`) : un lien déjà en
 * base qui ne le respecte pas n'est pas rendu du tout.
 */
// eslint-disable-next-line no-useless-escape -- motif gardé caractère pour caractère identique à celui de l'API
export const NEWS_CTA_URL_PATTERN = /^(?:https?:\/\/[^\s\\]+|\/(?![\/\\])[^\s\\]*)$/i;

// Images de couverture et du corps.
export const NEWS_IMAGE_MAX_SIZE_BYTES = 5 * 1024 * 1024;
export const NEWS_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const NEWS_IMAGE_ACCEPT = NEWS_IMAGE_MIME_TYPES.join(',');

// Vidéos téléversées dans le corps. Le serveur reconnaît le format au CONTENU du
// fichier, pas à son nom : un `.mov` renommé en `.mp4` reste refusé.
export const NEWS_VIDEO_MAX_SIZE_BYTES = 100 * 1024 * 1024;
export const NEWS_VIDEO_MIME_TYPES = ['video/mp4', 'video/webm'];
export const NEWS_VIDEO_EXTENSIONS = ['mp4', 'webm'];
export const NEWS_VIDEO_ACCEPT = 'video/mp4,video/webm,.mp4,.webm';
/**
 * Délai de la requête d'envoi d'une vidéo : 30 minutes. 100 Mo demandent
 * plusieurs minutes sur une connexion lente, bien au-delà des 30 secondes du
 * client par défaut ; le rédacteur garde la main avec le bouton d'annulation.
 */
export const NEWS_VIDEO_UPLOAD_TIMEOUT_MS = 30 * 60 * 1000;

/**
 * Chemin de la page d'une nouveauté sur les plateformes de diffusion :
 * `<plateforme>/nouveautes/<slug>`. Le formulaire d'administration affiche ce
 * préfixe au rédacteur et les quatre routeurs y montent la page de lecture : ne
 * pas le changer sans changer les deux.
 */
export const NEWS_PUBLIC_PATH_PREFIX = '/nouveautes/';

/** Chemin du fil des nouveautés, identique sur les quatre plateformes. */
export const NEWS_FEED_PATH = '/nouveautes';

/** Chemin de la page d'une nouveauté. */
export function newsPagePath(slug: string): string {
  return `${NEWS_PUBLIC_PATH_PREFIX}${encodeURIComponent(slug)}`;
}

/** Nouveautés par page dans le fil (l'API accepte 1 à 50). */
export const NEWS_FEED_PAGE_SIZE = 10;

/** Taille maximale d'un lot d'événements (`NEWS_EVENTS_MAX_BATCH` côté API). */
export const NEWS_EVENTS_MAX_BATCH = 20;

// ==================== MISES À JOUR LOGICIELLES ====================

/**
 * Image fixe des mises à jour logicielles (`category: 'TECH'`), servie par le site
 * (`public/images/news/`) : rien n'est enregistré côté API, la même image vaut
 * pour toutes les mises à jour, sur tous les environnements.
 */
export const TECH_NEWS_COVER_URL = '/images/news/mise-a-jour-logicielle.webp';

/**
 * Plateformes d'une mise à jour logicielle : les internes de l'admin et de la
 * gestion SAV. Le formulaire les cible toutes, toujours ; l'API refuse toute
 * autre plateforme (`TECH_NEWS_PLATFORMS` de `api/src/news/news.constants.ts`,
 * à garder identique).
 */
export const TECH_NEWS_PLATFORMS: readonly NewsPlatform[] = ['ADMIN', 'SAV'];

/** Libellés du type, côté gestion. */
export const NEWS_CATEGORY_LABELS: Record<NewsCategory, string> = {
  PRODUCT: 'Nouveauté',
  TECH: 'Mise à jour logicielle',
};

/**
 * Image de couverture à afficher et façon de l'afficher. Une mise à jour
 * logicielle montre toujours son image fixe EN ENTIER (`contain`) : ses quatre
 * coins portent du texte, qu'un recadrage couperait.
 */
export function newsCover(news: { category?: NewsCategory; coverImageUrl: string | null }): {
  url: string | null;
  contain: boolean;
} {
  if (news.category === 'TECH') {
    return { url: TECH_NEWS_COVER_URL, contain: true };
  }
  return { url: news.coverImageUrl, contain: false };
}
